import _ from 'lodash'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import ReportDataExtractor from 'scoreboard/reports/common/report-data-extractor'
import formatSummaryValue from 'scoreboard/datasets/overview/format-summary-value'
import clusterLabel from 'scoreboard/datasets/edit/fields/cluster-label'
import calendars from 'scoreboard/components/calendars'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import isGeographicOrRollup from '../../datasets/is-geographic-or-rollup'
import datasetFieldTimeFormat from 'scoreboard/datasets/dataset-field-time-format'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO, DATE, TIME, NUMBER, DATE_TIME, PERCENT, CALENDAR_PERIOD, CLUSTER } = DataType
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import i18n from '../../components/i18n'

// TODO: use get-aggregated-data-type.js instead
function coercedDataTypeFromAggregationType(aggregationType) {
	switch (aggregationType) {
		case 'percentage': return PERCENT
		case 'unique': return NUMBER
		case 'count': return NUMBER
		case 'min-date': case 'max-date': return DATE
		case 'min-time': case 'max-time': return TIME
	}
}

class DatasetReportDataExtractor extends ReportDataExtractor {

	constructor(reportModel) {
		super(reportModel)
		this.dataset = this.reportModel.get('dataset')
		//#44134 clone this (potentially large map) once, instead of n-stacks * m-rows
		this._treeItemsById = _.cloneDeep(this.reportModel.treeItemsById)
	}

	resolveContentAttributes(resultItem, columnModel) {
		const fieldId = columnModel.get('datasetFieldId')
		const field = fieldId && _.get(this.dataset.combinedFieldsById, fieldId)
		const resolvedAttributes = _.defaults({
			treeItemsById: this._treeItemsById,
			currencyCode: _.get(field, 'currencyCode')
		}, columnModel.attributes)

		const { aggregationType } = columnModel.attributes
		if (this.reportModel.isAggregating() &&
			!columnModel.isGrouping()
			&& aggregationType != 'none'
		) {
			resolvedAttributes.coercedDataType =
				coercedDataTypeFromAggregationType(aggregationType)
		}

		//#46248 ensure the column being grouped when aggregated is not using
		//an aggregated data type (because it's showing the grouped value not the
		//aggregated values)
		if (this.reportModel.isAggregating() && columnModel.isGrouping()) {
			resolvedAttributes.aggregationType = 'none'
		}

		return resolvedAttributes
	}

	extractData(result, resultItem, headerCell) {
		let { contentAttributes } = result
		let columnModel = headerCell.column
		let dataType = columnModel.get('dataType')
		let cellDatum =	this._getDatum(resultItem, columnModel)

		let value = cellDatum.value
		if (dataType == 'text' && !_.isArray(value)) {
			value = escapePreserveLineBreaks(cellDatum.value)
			result.exportValue = cellDatum.value
		}
		result.html = this.applyValueFormatting(value, contentAttributes)
		if (_.isNil(result.exportValue) && !isDateTimeDataType(dataType)) {
			result.rawValue = value
			result.exportValue = this.applyValueFormatting(value, contentAttributes, {
				abbreviated: false
			})
		}

		if (columnModel.defaultColumnData(headerCell.reportModel)?.supportsIndentHierarchy) {
			resultItem.depth = _.get(this._treeItemsById[value], 'depth', 0)
		}

		this._storeExportModel(result, value, contentAttributes)
		return result
	}

	toNonRepeatedAggregatedValue(result, resultItem, headerCell) {
		let columnClientId = headerCell.column.columnClientId()
		let datum = this._getDatum(resultItem, headerCell.column)
		return _.get(datum, `aggregationsByColumnClientId[${columnClientId}]`, '')
	}

	_getDatum(resultItem, columnModel) {
		let columnClientId = columnModel.columnClientId()
		if (columnModel.get('calculated') === true) {
			return _.get(resultItem, `calculatedValuesByColumnClientId[${columnClientId}]`, {})
		}
		return _.get(resultItem, `dataByColumnClientId[${columnClientId}]`, {})
	}

	formatForGroupHeader(grouping, columnAttributes) {
		return this.applyValueFormatting(grouping.displayValue, _.extend({
			treeItemsById: this._treeItemsById
		}, columnAttributes))
	}

	wrapGroupHeaderValue(value, columnAttributes) {
		// if the values is not already wrapped in a span, then wrap it here
		// it will be if it an abbreviated numeric type as it will have a
		// tooltip
		const { dataType, coercedDataType, abbreviated, summaryType } = columnAttributes
		if (!isNumericDataType(coercedDataType ?? dataType) || !abbreviated
			// #54747 if this is a range header, the data type will be numeric,
			// but the value will be a text value so we still need to wrap it
			|| summaryType == 'range') {
			return super.wrapGroupHeaderValue(value, columnAttributes)
		}
		return value
	}

	applyValueFormatting(value, columnAttributes = {}, overrides = {}) {
		const dataType = overrides.dataType || columnAttributes.coercedDataType ||
			columnAttributes.dataType
		let aggregationType = overrides.aggregationType || columnAttributes.aggregationType
		if (_.includes([TIME, DATE_TIME], dataType)) {
			columnAttributes.timeFormat = this._getDatasetFieldTimeFormat(
				columnAttributes.datasetFieldId
			)
		}

		// if there is only one column being displayed, don't aggregate the
		// results
		const activeColumnIds = _(this.reportModel.allColumns())
			.filter(c => !c.get('newBlankColumn'))
			.map(c => c.get('datasetFieldId'))
			.uniq()
			.value()

		//the aggregationType for datasets can determine the actual dataType
		//needed to property resolve the value even when the report itself is not
		//actually aggregating (this is because field itself can implicitly be
		//aggregating from the "many" side of a dataset link), so unless the aggregation
		//type is set to none, we need to indicate this column is aggregating
		//see dataset-report-model._getAggregationTypeForFieldId
		if (aggregationType != 'none' && !columnAttributes.groupingId
			&& dataType != YES_NO) {
			overrides.aggregated = true
		} else {
			if (dataType == CALENDAR_PERIOD) {
				return _.get(calendars.periodsById[value], 'label', '')
			}

			if (dataType === CLUSTER) {
				const field = this.reportModel.getDatasetField(columnAttributes)
				return clusterLabel(value, field.clusterLabels)
			}

			if (isGeographicOrRollup(dataType)) {
				if (_.isNil(value)) {
					// #59971 "Blank" values have special CSS styles
					return `<span class="blank-value">${i18n.get('common.blank')}</span>`
				}
				//used the coerced data type to ensure linked fields are resolved
				//with the correct type
				let resolvedAttributes = _.defaults({dataType}, columnAttributes)
				// #40580 rollup values from the server may come back as String
				// labels or treeIDs, so don't do a parseInt on the value
				return formatSummaryValue(resolvedAttributes, value, {
					treeItemsById: columnAttributes.treeItemsById,
					dataset: this.dataset
				})
			}
		}

		return ReportDataExtractor.prototype.applyValueFormatting.call(this,
			value, columnAttributes, overrides)
	}

	_getDatasetFieldTimeFormat(fieldId) {
		const field = this.dataset.combinedFieldsById[fieldId]
		return datasetFieldTimeFormat(field)
	}

	_summaryValueFormattingContext(columnAttributes) {
		const fieldId = columnAttributes.datasetFieldId
		const field = this.dataset.combinedFieldsById[fieldId]
		return {
			datasetField: field,
			treeItemsById: this._treeItemsById
		}
	}

}

export default DatasetReportDataExtractor
DatasetReportDataExtractor.__filename__ = 'reports/dataset-report/dataset-report-data-extractor.js'
